<script lang="ts">
  import SearchIcon from "$lib/icons/SearchIcon.svelte";
  import Button from "$lib/elements/Button.svelte";
  import FlashIcon from "$lib/icons/FlashIcon.svelte";
  import ProviderDropDown from "$lib/components/ModalContents/Search/ProviderDropDown.svelte";
  import type {IGame} from "$lib/interfaces/IGame";
  import {gameService} from "$lib/services/game.service";
  import GameCard from "$lib/components/GameCard.svelte";
  import {goto} from "$app/navigation";
  import {locale, translate} from "$lib/i18n";
  import {closeDialog} from "$lib/stores/dialogStores";
  import {onMount} from "svelte";
  import LoadMoreButton from "$lib/elements/LoadMoreButton.svelte";

  // Initialize all state variables at the top
  let games: IGame[] = $state([]);
  let totalPages = 0;
  let totalElements = $state(32);
  let currentPage = $state(0);
  let search: HTMLInputElement;
  let searchValue = '';
  let loaded = $state(false);
  let providerId = '';
  let platform: 'MOBILE_SUPPORT' | 'DESKTOP_SUPPORT' = 'DESKTOP_SUPPORT';
  let searchTimeout: ReturnType<typeof setTimeout>;
  let isSearching = $state(false);
  let noResults = $state(false);

  // Generate skeleton cards for loading state
  const skeletonCards = Array(12).fill(null);

  // Initialize the promise immediately
  let promise = $state(getGames());

  onMount(() => {
    platform = window.innerWidth < 768 ? 'MOBILE_SUPPORT' : 'DESKTOP_SUPPORT';
    // Focus the search input when modal opens
    setTimeout(() => {
      if (search) search.focus();
    }, 100);
  });

  function loadMore() {
    currentPage += 1;
    getGames();
  }

  function searchGames(value = '') {
    // Clear previous timeout to debounce search
    if (searchTimeout) clearTimeout(searchTimeout);

    isSearching = true;

    // Set a timeout to avoid making API calls on every keystroke
    searchTimeout = setTimeout(() => {
      searchValue = value;
      currentPage = 0;
      games = [];
      promise = getGames(true);
    }, 300); // 300ms debounce
  }

  async function getGames(fromSearch = false) {
    loaded = false;

    try {
      const res = await gameService.getGames(searchValue, currentPage, providerId, platform);

      if (fromSearch) {
        games = res.data.data;
      } else {
        games = [...games, ...res.data.data];
      }

      totalPages = res.data.totalPages;
      totalElements = res.data.totalElements;

      // Check if no results were found
      noResults = fromSearch && res.data.data.length === 0;

      return res;
    } catch (error) {
      console.error("Error fetching games:", error);
      throw error;
    } finally {
      loaded = true;
      isSearching = false;
    }
  }

  function getRandomGame() {
    gameService.getRandomGames().then((resRandomGame) => {
      goto(`/${$locale}/game/${resRandomGame.data.data.slug}`);
      closeDialog('SearchModal');
    });
  }

  function selectProvider(e: any) {
    providerId = e.detail.id;
    searchGames('');
  }
</script>

<div class="p-4 md:p-6 bg-gradient-to-b from-primary-dark/90 to-primary-dark rounded-xl">
  <!-- Header with title -->
  <div class="flex items-center justify-between mb-6">
    <h2 class="text-2xl font-bold text-white gradient-text-fire">{$translate('Game Explorer')}</h2>

    <!-- Random game button for larger screens -->
    <button
            onclick={() => getRandomGame()}
            class="hidden md:flex items-center gap-2 bg-gradient-to-r from-primary-fire to-yellow-500 text-white px-5 py-2.5 rounded-full transition-all hover:shadow-lg hover:shadow-primary-fire/20 hover:scale-105"
    >
      <FlashIcon />
      <span>{$translate('Im Feeling Lucky')}</span>
              </button>
              </div>

              <!-- Search controls -->
              <div class="flex flex-col md:flex-row gap-4 mb-6">
              <!-- Search input -->
              <div class="relative flex-grow">
              <div class="absolute left-4 top-1/2 transform -translate-y-1/2 text-primary-lightBlue">
              <SearchIcon cssClass="stroke-primary-lightBlue" />
              </div>

              <input
              bind:this={search}
              oninput={(e) => searchGames(e?.target?.value)}
              type="text"
              class="bg-primary-blue/10 py-3.5 px-12 outline-none text-white w-full rounded-xl border border-primary-blue/20 focus:border-primary-fire/30 focus:shadow-lg focus:shadow-primary-fire/10 transition-all placeholder-primary-lightBlue/70"
              placeholder={$translate('Search for games...')}
              />

              <!-- Clear search button -->
              {#if searchValue}
              <button
              onclick={() => searchGames('')}
              class="absolute right-4 top-1/2 transform -translate-y-1/2 text-primary-lightBlue hover:text-white transition-colors"
              aria-label="Clear search"
              >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              </button>
              {/if}
              </div>

              <!-- Provider dropdown -->
              <div class="min-w-[180px]">
              <ProviderDropDown id="searchProviderDropdown" on:selected={(e) => selectProvider(e)} />
              </div>

              <!-- Random game button for mobile -->
              <button
              onclick={() => getRandomGame()}
              class="md:hidden flex items-center justify-center gap-2 bg-gradient-to-r from-primary-fire to-yellow-500 text-white px-4 py-3 rounded-xl shadow-md"
              >
              <FlashIcon />
              <span>{$translate('Im Feeling Lucky')}</span>
    </button>
  </div>

  <!-- Search status indicator -->
  {#if isSearching}
    <div class="flex items-center gap-2 text-primary-lightBlue mb-4 animate-pulse">
      <div class="w-4 h-4 border-2 border-primary-lightBlue border-t-transparent rounded-full animate-spin"></div>
      <span>{$translate('Searching...')}</span>
    </div>
  {:else if searchValue}
    <div class="flex items-center gap-2 text-primary-lightBlue mb-4">
      <span>
        {#if noResults}
          {$translate('No results found for')} "<span class="text-primary-fire">{searchValue}</span>"
        {:else}
          {$translate('Showing results for')} "<span class="text-primary-fire">{searchValue}</span>"
        {/if}
      </span>
    </div>
  {/if}

  <!-- Games grid with scrollable container -->
  <div class="no-scrollbar max-h-[calc(80vh-180px)] overflow-y-auto rounded-xl p-1">
    {#await promise}
      <!-- Skeleton loading state -->
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {#each skeletonCards as _, i}
          <div class="bg-primary-blue/5 rounded-xl overflow-hidden aspect-[3/4] relative group">
            <div class="absolute inset-0 bg-gradient-to-b from-primary-blue/5 to-primary-blue/20 skeleton-pulse"></div>

            <!-- Skeleton game title -->
            <div class="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-primary-dark to-transparent">
              <div class="h-3 w-2/3 bg-primary-blue/20 rounded mb-2 skeleton-pulse"></div>
              <div class="h-3 w-1/2 bg-primary-blue/20 rounded skeleton-pulse"></div>
            </div>
          </div>
        {/each}
      </div>
    {:then gamesLoaded}
      {#if games.length > 0}
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {#each games as game (game.id)}
            <div class="game-card-wrapper" style="--delay: {games.indexOf(game) % 12 * 50}ms">
              <GameCard {game} />
            </div>
          {/each}
        </div>

        <!-- Load more button -->
        <div class="mt-8 flex justify-center">
          <LoadMoreButton currentPage={currentPage+1} totalElements={totalElements} loadMore={loadMore} loading={!loaded} />
        </div>
      {:else if noResults}
        <!-- No results state -->
        <div class="flex flex-col items-center justify-center py-12 text-center bg-primary-blue/5 rounded-xl">
          <div class="w-20 h-20 bg-primary-blue/10 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-primary-lightBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-xl font-medium text-white mb-2">{$translate('No Games Found')}</h3>
          <p class="text-primary-lightBlue max-w-md">
            {$translate('We couldn\'t find any games matching your search. Try different keywords or browse by provider.')}
          </p>

          <!-- Suggestions -->
          <div class="mt-6 flex flex-wrap gap-2 justify-center">
            <button
                    onclick={() => searchGames('slots')}
                    class="px-4 py-2 bg-primary-blue/10 hover:bg-primary-blue/20 rounded-full text-primary-lightBlue transition-colors"
            >
              Slots
            </button>
            <button
                    onclick={() => searchGames('jackpot')}
                    class="px-4 py-2 bg-primary-blue/10 hover:bg-primary-blue/20 rounded-full text-primary-lightBlue transition-colors"
            >
              Jackpot
            </button>
            <button
                    onclick={() => searchGames('table')}
                    class="px-4 py-2 bg-primary-blue/10 hover:bg-primary-blue/20 rounded-full text-primary-lightBlue transition-colors"
            >
              Table Games
            </button>
          </div>
        </div>
      {/if}
    {:catch error}
      <!-- Error state -->
      <div class="flex flex-col items-center justify-center py-12 text-center bg-primary-fire/5 rounded-xl">
        <div class="w-20 h-20 bg-primary-fire/10 rounded-full flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-primary-fire" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 class="text-xl font-medium text-white mb-2">{$translate('Something Went Wrong')}</h3>
        <p class="text-primary-lightBlue max-w-md">
          {error.message || $translate('We encountered an error while searching for games. Please try again later.')}
        </p>
        <button
                onclick={() => promise = getGames(true)}
                class="mt-4 px-6 py-2 bg-gradient-to-r from-primary-fire to-yellow-500 text-white rounded-full hover:opacity-90 transition-colors shadow-md"
        >
          {$translate('Try Again')}
        </button>
      </div>
    {/await}
  </div>
</div>

<style lang="scss">
  .no-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(59, 130, 246, 0.3) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(59, 130, 246, 0.3);
      border-radius: 20px;
    }
  }

  .game-card-wrapper {
    opacity: 0;
    transform: translateY(10px);
    animation: cardFadeIn 0.5s ease-in-out forwards;
    animation-delay: var(--delay, 0ms);
  }

  @keyframes cardFadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Skeleton loading animation */
  .skeleton-pulse {
    position: relative;
    overflow: hidden;

    &::after {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg,
              rgba(255, 255, 255, 0.0) 0%,
              rgba(255, 255, 255, 0.1) 50%,
              rgba(255, 255, 255, 0.0) 100%);
      animation: shimmer 1.5s infinite;
    }
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  /* Gradient text effect */
  :global(.gradient-text-fire) {
    background: linear-gradient(90deg, #FF4D4D 0%, #F9CB28 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    display: inline-block;
  }

  /* Enhanced hover effects for game cards */
  :global(.game-card-wrapper:hover) {
    transform: translateY(-5px);
    transition: transform 0.3s ease;
  }
</style>